<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-full"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-full"
  >
    <div
      v-if="showNotification"
      class="fixed top-0 left-0 right-0 z-50 px-4 py-3"
      :class="notificationClasses"
    >
      <div class="flex items-center justify-center space-x-2">
        <!-- Wifi Icon -->
        <svg v-if="statusIcon === 'wifi'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
        </svg>
        <!-- Wifi Off Icon -->
        <svg v-else-if="statusIcon === 'wifi-off'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-12.728 12.728m0-12.728l12.728 12.728M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01" />
        </svg>
        <!-- Exclamation Icon -->
        <svg v-else-if="statusIcon === 'exclamation-triangle'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <!-- Check Icon -->
        <svg v-else-if="statusIcon === 'check'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
        <span class="text-sm font-medium">{{ statusMessage }}</span>
        <button
          v-if="showRetryButton"
          @click="handleRetry"
          class="ml-2 px-3 py-1 text-xs font-medium rounded-md transition-colors"
          :class="retryButtonClasses"
        >
          Retry
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useNetworkStatus } from '@/composables/useNetworkStatus'
import { logger, createLogger } from '@/utils/logger'

const fileLogger = createLogger('UINetworkStatus')

// Icons - using template refs instead of JSX
const WifiIcon = 'wifi'
const WifiOffIcon = 'wifi-off'
const ExclamationIcon = 'exclamation-triangle'
const CheckIcon = 'check'

const { networkStatus, resetRetryCount } = useNetworkStatus()

const showNotification = ref(false)
const notificationTimeout = ref<number | null>(null)

const statusIcon = computed(() => {
  if (!networkStatus.value.isOnline) {
    return WifiOffIcon
  } else if (networkStatus.value.isSlowConnection) {
    return ExclamationIcon
  } else {
    return WifiIcon
  }
})

const statusMessage = computed(() => {
  if (!networkStatus.value.isOnline) {
    return 'You are currently offline'
  } else if (networkStatus.value.isSlowConnection) {
    return 'Slow connection detected'
  } else {
    return 'Connection restored'
  }
})

const notificationClasses = computed(() => {
  if (!networkStatus.value.isOnline) {
    return 'bg-red-500 text-white'
  } else if (networkStatus.value.isSlowConnection) {
    return 'bg-yellow-500 text-white'
  } else {
    return 'bg-green-500 text-white'
  }
})

const showRetryButton = computed(() => {
  return !networkStatus.value.isOnline || networkStatus.value.isSlowConnection
})

const retryButtonClasses = computed(() => {
  if (!networkStatus.value.isOnline) {
    return 'bg-red-600 hover:bg-red-700 text-white'
  } else {
    return 'bg-yellow-600 hover:bg-yellow-700 text-white'
  }
})

const handleRetry = () => {
  fileLogger.log('Retry button clicked')
  resetRetryCount()
  
  // Emit retry event for parent components to handle
  // This could trigger a refresh of data or retry failed requests
  window.dispatchEvent(new CustomEvent('network-retry'))
  
  // Hide notification after retry
  showNotification.value = false
  if (notificationTimeout.value) {
    clearTimeout(notificationTimeout.value)
  }
}

// Watch for network status changes
watch(
  () => networkStatus.value.isOnline,
  (isOnline, wasOnline) => {
    if (wasOnline !== undefined) { // Don't show on initial load
      showNotification.value = true
      
      // Auto-hide notification after 5 seconds for online status
      if (isOnline) {
        notificationTimeout.value = window.setTimeout(() => {
          showNotification.value = false
        }, 5000)
      }
    }
  }
)

watch(
  () => networkStatus.value.isSlowConnection,
  (isSlow) => {
    if (isSlow) {
      showNotification.value = true
      
      // Auto-hide slow connection notification after 3 seconds
      notificationTimeout.value = window.setTimeout(() => {
        showNotification.value = false
      }, 3000)
    }
  }
)

// Cleanup timeout on unmount
import { onUnmounted } from 'vue'
onUnmounted(() => {
  if (notificationTimeout.value) {
    clearTimeout(notificationTimeout.value)
  }
})
</script>
